import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { plural } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { ServiceDepartmentModule } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiIconName } from 'noddi-ui-common';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiSkeleton } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useAuthContext, useIsGroupAdmin } from 'noddi-web';
import type { ReactNode } from 'react';
import { useAvailableBrandModules } from '../../../hooks/useAvailableBrandModules';
import { useBrandCapabilities } from '../../../hooks/useBrandCapabilities';
import { useNewBookingPath } from '../../../hooks/useNewBookingPath';
import { useClearBookingDrafts } from '../../../hooks/useStartNewBooking';

type QuickAction = {
  icon: NoddiIconName;
  title: ReactNode;
  sub: ReactNode;
  to: string;
  onClick?: () => void;
};

const SKELETON_TILE_COUNT = 4;

export const QuickActions = () => {
  const { currentUserGroupId, userData } = useAuthContext();
  const {
    isGroupAdmin,
    isLoading: isGroupAdminLoading,
    error: isGroupAdminError
  } = useIsGroupAdmin(currentUserGroupId, userData?.user.id);
  const clearBookingDrafts = useClearBookingDrafts();
  const { availableModules, isLoading: areModulesLoading, error: modulesError } = useAvailableBrandModules();
  const { path: newBookingPath } = useNewBookingPath();
  const { canCreateBooking } = useBrandCapabilities();

  const {
    data: fleetCars,
    isPending: areFleetCarsPending,
    error: fleetCarsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupCarsForBooking,
    input: { userGroupId: currentUserGroupId!, pageSize: 1, pageIndex: 0 }
  });

  if (fleetCarsError) {
    return <ApiErrorPageWithTrans error={fleetCarsError} />;
  }

  if (modulesError) {
    return <ApiErrorPageWithTrans error={modulesError} />;
  }
  if (isGroupAdminError) {
    return <ApiErrorPageWithTrans error={isGroupAdminError} />;
  }

  const carCount = fleetCars?.count ?? 0;
  const carCountLabel = plural(carCount, { one: '# car', other: '# cars' });

  const startNewBooking = () => {
    clearBookingDrafts();
  };

  const optionalActions: (QuickAction | null)[] = [
    canCreateBooking
      ? {
          icon: 'Plus',
          title: <Trans>New booking</Trans>,
          sub: <Trans>For one or more cars</Trans>,
          to: newBookingPath,
          onClick: startNewBooking
        }
      : null,
    availableModules.has(ServiceDepartmentModule.TIRE_SALES)
      ? {
          icon: 'Mail',
          title: <Trans>Tire offer</Trans>,
          sub: <Trans>Review offers for your fleet</Trans>,
          to: customerRoutes.tireOfferHomePage.getPath()
        }
      : null,
    {
      icon: 'Car',
      title: <Trans>Fleet</Trans>,
      sub: carCountLabel,
      to: customerRoutes.fleet.getPath()
    },
    isGroupAdmin === true
      ? {
          icon: 'Users',
          title: <Trans>Members and drivers</Trans>,
          sub: <Trans>Manage members, who drives each car, and SMS notifications</Trans>,
          to: customerRoutes.organizationMembers.getPath()
        }
      : null
  ];

  const actions = optionalActions.filter((action): action is QuickAction => action !== null);
  const isLoading = areModulesLoading || areFleetCarsPending || isGroupAdminLoading;
  // How many tiles the grid will hold is only known once the modules resolve, so the
  // placeholder shows the common case rather than guessing per brand.
  const tileCount = isLoading ? SKELETON_TILE_COUNT : actions.length;
  const getTileBorders = (index: number) => ({
    hasRightBorder: index % 2 === 0 && index + 1 < tileCount,
    hasBottomBorder: Math.floor(index / 2) !== Math.ceil(tileCount / 2) - 1,
    isLastTile: index === tileCount - 1
  });

  return (
    <div className='flex flex-col gap-2 md:gap-4'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>
        <Trans>Quick actions</Trans>
      </h2>

      <NoddiBasicCard className='flex flex-col overflow-hidden p-0'>
        {/* One tile per row on phones - two 165px columns clipped every label to an ellipsis. */}
        <div className='grid grid-cols-1 sm:grid-cols-2'>
          {isLoading
            ? Array.from({ length: tileCount }, (_, index) => (
                <QuickActionTileSkeleton key={index} {...getTileBorders(index)} />
              ))
            : actions.map((action, index) => (
                <QuickActionTile key={action.to + index} action={action} {...getTileBorders(index)} />
              ))}
        </div>
      </NoddiBasicCard>
    </div>
  );
};

const QuickActionTileSkeleton = ({
  hasRightBorder,
  hasBottomBorder,
  isLastTile
}: {
  hasRightBorder: boolean;
  hasBottomBorder: boolean;
  isLastTile: boolean;
}) => (
  <div
    className={cn(
      'flex flex-row items-center gap-4 p-4',
      !isLastTile && 'border-b border-border',
      hasRightBorder && 'sm:border-r sm:border-border',
      hasBottomBorder ? 'sm:border-b sm:border-border' : 'sm:border-b-0'
    )}
  >
    <NoddiSkeleton className='size-9 shrink-0 rounded-md' />
    <div className='flex min-w-0 flex-1 flex-col gap-2'>
      <NoddiSkeleton className='h-3 w-24' />
      <NoddiSkeleton className='h-3 w-32' />
    </div>
  </div>
);

const QuickActionTile = ({
  action,
  hasRightBorder,
  hasBottomBorder,
  isLastTile
}: {
  action: QuickAction;
  /** Grid-row flags; both only apply from `sm` up, where the tiles sit two per row. */
  hasRightBorder: boolean;
  hasBottomBorder: boolean;
  isLastTile: boolean;
}) => {
  return (
    <NoddiBasicCard
      to={action.to}
      onClick={action.onClick}
      className={cn(
        'flex flex-row items-center gap-4 rounded-none border-0 p-4 shadow-none',
        // Stacked on phones, so the divider goes between every tile but the last.
        !isLastTile && 'border-b border-border',
        hasRightBorder && 'sm:border-r sm:border-border',
        hasBottomBorder ? 'sm:border-b sm:border-border' : 'sm:border-b-0'
      )}
    >
      <div className='flex size-9 shrink-0 items-center justify-center rounded-md bg-card-light-background text-primary'>
        <NoddiIcon name={action.icon} size='small' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='text-sm font-semibold text-primary'>{action.title}</span>
        <span className='text-xs text-muted-foreground'>{action.sub}</span>
      </div>
      <NoddiIcon name='ChevronRight' size='small' className='text-muted-foreground' />
    </NoddiBasicCard>
  );
};
